import { useCallback, useEffect, useMemo, useState } from "react";
import { useBinancePositions } from "../../hooks/api/binance/useBinancePositions";
import { useBybitPositions } from "../../hooks/api/bybit/useBybitPositions";
import { useHyperliquidPositions } from "../../hooks/api/hyperliquid/useHyperliquidPositions";
import { LighterCoverage } from "../LighterCoverage";
import { useLighterPositions } from "../../hooks/api/lighter/useLighterPositions";
import { useAccount } from "../../hooks/api/admin/useAccount";
import { useMarkets } from "../../hooks/api/aevo/useMarkets";
import { INTERNAL_PERPS_LONG_ADDRESS, INTERNAL_PERPS_SHORT_ADDRESS } from "../../constants/admin";
import { standardizeAevoPositions } from "../../utils/positions/aevo";
import { standardizeBinancePositions } from "../../utils/positions/binance";
import { standardizeBybitPositions } from "../../utils/positions/bybit";
import { standardizeHyperliquidPositions } from "../../utils/positions/hyperliquid";
import { standardizeLighterPositions } from "../../utils/positions/lighter";
import { aggregatePositions } from "../../utils/positions";
import { computePerpAggregatedPositions } from "../../utils/perpAggregatedPositions";
import { buildPerpOverviewRows } from "../../utils/perpDataOverview";
import { PerpDataOverviewTable } from "./PerpDataOverviewTable";
import { PerpDataAssetDetail } from "./PerpDataAssetDetail";
import {
  ContentContainer,
  FilterGroup,
  FiltersWrapper,
  HeaderRow,
  PageTitle,
  PnLWrapper,
  TableContainer,
} from "../PnL/style";
import { BORDER_COLORS, TEXT_COLORS } from "../../constants/design/colors";

const PAGE_SIZE = 15;

export function PerpData() {
  const [selectedAsset, setSelectedAsset] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [assetFilter, setAssetFilter] = useState("");

  const { data: marketsData } = useMarkets();

  const {
    data: binancePositionsData = [],
    isLoading: isBinancePositionLoading,
    isValidating: isBinancePositionValidating,
  } = useBinancePositions(INTERNAL_PERPS_SHORT_ADDRESS);

  const {
    data: bybitPositionsData = [],
    isLoading: isBybitPositionLoading,
    isValidating: isBybitPositionValidating,
  } = useBybitPositions(INTERNAL_PERPS_SHORT_ADDRESS);

  const {
    data: hyperliquidPositionsData = [],
    isLoading: isHyperliquidPositionLoading,
    isValidating: isHyperliquidPositionValidating,
  } = useHyperliquidPositions(INTERNAL_PERPS_SHORT_ADDRESS);

  const lighterRobinhood = useLighterPositions("robinhood");
  const lighterMainnet = useLighterPositions("mainnet");
  const lighterComplete = lighterRobinhood.available && lighterMainnet.available;
  const { data: lighterMainnetPositionsData = [] } = lighterMainnet;
  const {
    data: lighterPositionsData = [],
    isLoading: isLighterPositionLoading,
    isValidating: isLighterPositionValidating,
  } = lighterRobinhood;

  const {
    data: perpsShortAccount,
    isLoading: isPerpsShortLoading,
    isValidating: isPerpsShortValidating,
  } = useAccount(INTERNAL_PERPS_SHORT_ADDRESS);

  const {
    data: perpsLongAccount,
    isLoading: isPerpsLongLoading,
    isValidating: isPerpsLongValidating,
  } = useAccount(INTERNAL_PERPS_LONG_ADDRESS);

  const perpPositions = useMemo(() => {
    const binancePositions = standardizeBinancePositions(binancePositionsData);
    const bybitPositions = standardizeBybitPositions(bybitPositionsData);
    const hyperliquidPositions = standardizeHyperliquidPositions(hyperliquidPositionsData);
    const lighterPositions = standardizeLighterPositions(lighterPositionsData);
    const lighterMainnetPositions = standardizeLighterPositions(lighterMainnetPositionsData, "mainnet");
    const perpsShortPositions = standardizeAevoPositions(perpsShortAccount?.positions || [], undefined);
    const perpsLongPositions = standardizeAevoPositions(perpsLongAccount?.positions || [], undefined);
    const aevoPositions = aggregatePositions([...perpsShortPositions, ...perpsLongPositions]);

    return {
      aevoPositions,
      binancePositions,
      bybitPositions,
      hyperliquidPositions,
      lighterPositions,
      lighterMainnetPositions,
    };
  }, [
    binancePositionsData,
    bybitPositionsData,
    hyperliquidPositionsData,
    lighterPositionsData,
    lighterMainnetPositionsData,
    perpsShortAccount,
    perpsLongAccount,
  ]);

  const aggregatedPositions = useMemo(
    () =>
      computePerpAggregatedPositions(
        perpPositions.aevoPositions,
        perpPositions.binancePositions,
        perpPositions.bybitPositions,
        perpPositions.hyperliquidPositions,
        perpPositions.lighterPositions,
        marketsData,
        perpPositions.lighterMainnetPositions
      ),
    [perpPositions, marketsData]
  );

  const overviewRows = useMemo(
    () => buildPerpOverviewRows(aggregatedPositions),
    [aggregatedPositions]
  );

  const filteredRows = useMemo(() => {
    const query = assetFilter.trim().toUpperCase();
    if (!query) return overviewRows;
    return overviewRows.filter((row) => row.asset.toUpperCase().includes(query));
  }, [overviewRows, assetFilter]);

  const isPositionsLoading = useMemo(
    () =>
      isBinancePositionLoading ||
      isBybitPositionLoading ||
      isPerpsShortLoading ||
      isPerpsLongLoading ||
      isHyperliquidPositionLoading ||
      isLighterPositionLoading ||
      isBinancePositionValidating ||
      isBybitPositionValidating ||
      isPerpsShortValidating ||
      isPerpsLongValidating ||
      isHyperliquidPositionValidating ||
      isLighterPositionValidating,
    [
      isBinancePositionLoading,
      isBybitPositionLoading,
      isPerpsShortLoading,
      isPerpsLongLoading,
      isHyperliquidPositionLoading,
      isLighterPositionLoading,
      isBinancePositionValidating,
      isBybitPositionValidating,
      isPerpsShortValidating,
      isPerpsLongValidating,
      isHyperliquidPositionValidating,
      isLighterPositionValidating,
    ]
  );

  useEffect(() => {
    setPage(1);
  }, [assetFilter]);

  const handleSelectAsset = useCallback((asset: string) => {
    setSelectedAsset(asset);
    setPage(1);
  }, []);

  const handleBack = useCallback(() => {
    setSelectedAsset(null);
    setPage(1);
  }, []);

  if (selectedAsset) {
    return (
      <PerpDataAssetDetail
        asset={selectedAsset}
        onBack={handleBack}
        perpPositions={perpPositions}
        isPositionsLoading={isPositionsLoading}
        lighterCoverage={<LighterCoverage robinhood={lighterRobinhood} mainnet={lighterMainnet} />}
        positionsComplete={lighterComplete}
      />
    );
  }

  return (
    <PnLWrapper>
      <ContentContainer>
        <HeaderRow>
          <PageTitle>Perp Data</PageTitle>
        </HeaderRow>

        <p style={{ margin: "0 0 16px", opacity: 0.75, fontSize: 14 }}>
          Select an asset to view delta breakdown, PnL, funding, and trade chart. Strategy: PERP MM.
        </p>

        <FiltersWrapper>
          <FilterGroup>
            <span>Filter asset</span>
            <input
              type="text"
              value={assetFilter}
              onChange={(e) => setAssetFilter(e.target.value)}
              placeholder="e.g. ETH, BTC"
              style={{
                padding: "8px 12px",
                borderRadius: 6,
                border: `1px solid ${BORDER_COLORS.one}`,
                background: "transparent",
                color: TEXT_COLORS.one,
                minWidth: 180,
              }}
            />
          </FilterGroup>
        </FiltersWrapper>

        <LighterCoverage robinhood={lighterRobinhood} mainnet={lighterMainnet} />
        {lighterComplete && <TableContainer>
          <PerpDataOverviewTable
            rows={filteredRows}
            isLoading={isPositionsLoading}
            page={page}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
            onSelectAsset={handleSelectAsset}
          />
        </TableContainer>}
      </ContentContainer>
    </PnLWrapper>
  );
}
